import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

interface SEOData {
  title: string;
  description: string;
  keywords?: string;
  canonicalUrl?: string;
  ogTitle?: string;
  ogDescription?: string;
  ogImage?: string;
  ogType?: string;
  twitterCard?: string;
  twitterTitle?: string;
  twitterDescription?: string;
  twitterImage?: string;
  noIndex?: boolean;
}

const DEFAULT_SEO: SEOData = {
  title: 'WillTheyConvert - Validate Your Product Ideas',
  description: 'Test if people will pay for your product idea before building it. Create fake door tests in minutes and validate your startup idea with real users.',
  keywords: 'startup validation, fake door testing, MVP testing, product validation, idea validation, startup testing',
  ogImage: '/images/og-image.jpg',
  ogType: 'website',
  twitterCard: 'summary_large_image'
};

const ROUTE_SEO: Record<string, SEOData> = {
  '/': DEFAULT_SEO,
  '/auth': {
    title: 'Sign In - WillTheyConvert',
    description: 'Sign in to your WillTheyConvert account to manage your product validation tests and view analytics.',
  },
  '/dashboard': {
    title: 'Dashboard - WillTheyConvert',
    description: 'Manage your validation tests and view real-time analytics for your product ideas.',
    noIndex: true
  },
  '/create': {
    title: 'Create New Test - WillTheyConvert',
    description: 'Create a new validation test for your product idea. Choose from multiple test types and customize your flow.',
    noIndex: true
  },
  '/analytics': {
    title: 'Analytics - WillTheyConvert',
    description: 'View detailed analytics and insights for your product validation tests.',
    noIndex: true
  },
  '/features': {
    title: 'Features - Validate Your Startup Ideas Without Writing Code',
    description: 'Discover our powerful features for validating startup ideas, from fake door testing to analytics and insights. Get real customer feedback before you build.',
    keywords: 'startup validation, fake door testing, product testing, product validation, startup idea validation, MVP testing',
    canonicalUrl: '/features',
    ogTitle: 'Powerful Features to Validate Your Startup Idea | WillTheyConvert',
    ogDescription: 'Test your business ideas without writing code. Our platform helps you measure real demand, collect emails, and get feedback before investing in development.'
  },
  '/guides': {
    title: 'Guides & Documentation - Test Your Product Ideas Without Code',
    description: 'Learn how WillTheyConvert helps founders, product managers, and entrepreneurs validate their startup ideas without writing a single line of code.',
    keywords: 'startup validation process, how to validate startup ideas, product testing without code, startup idea validation, fake door testing, implementation guides',
    canonicalUrl: '/guides',
    ogTitle: 'Complete Guide to Product Validation & Implementation',
    ogDescription: 'See how founders, product managers, and indie makers use our platform to test demand for their ideas before investing in development.'
  },
  '/case-studies': {
    title: 'Startup Validation Case Studies | Real-World Examples',
    description: 'Explore real-world examples of successful startup idea validation. Case studies from SaaS, e-commerce, and marketplace startups that validated before building.',
    keywords: 'startup validation case studies, idea validation examples, product validation, market testing examples',
    canonicalUrl: '/case-studies',
    ogTitle: 'Startup Validation Case Studies | Learn from Success Stories',
    ogDescription: 'See how Dropbox, Zappos, and Airbnb validated their startup ideas before building. Real metrics, strategies, and insights you can apply today.'
  },
  '/validate-startup': {
    title: 'Validate Your Startup Idea Before Building',
    description: "Don't waste time and money building products nobody wants. Our validation platform helps you test if real users will convert before writing a single line of code.",
    keywords: 'startup validation, product-market fit, customer validation, lean startup, MVP, startup success, idea validation, pre-launch testing, market validation, fake door testing',
    canonicalUrl: '/validate-startup'
  },
  '/pre-launch-guide': {
    title: 'Pre-Launch Guide - How to Launch Your Startup Successfully',
    description: 'Learn how to successfully launch your startup with our comprehensive pre-launch guide. Discover strategies, tips, and best practices for idea validation and market entry.',
    canonicalUrl: '/pre-launch-guide'
  },
  '/terms': {
    title: 'Terms of Service - WillTheyConvert',
    description: 'Read our terms of service to understand the rules and guidelines for using WillTheyConvert.',
    canonicalUrl: '/terms'
  },
  '/privacy': {
    title: 'Privacy Policy - WillTheyConvert',
    description: 'Learn how we collect, use, and protect your personal information when you use WillTheyConvert.',
    canonicalUrl: '/privacy'
  },
  '/demos': {
    title: 'Flow Demos - Interactive Validation Test Examples | WillTheyConvert',
    description: 'Explore interactive demos of all validation flows. See payment tests, waitlists, pricing tables, and more in action before creating your own.',
    keywords: 'validation demos, fake door testing examples, payment flow demo, waitlist demo, pricing table demo',
    canonicalUrl: '/demos',
    ogTitle: 'Interactive Validation Flow Demos | WillTheyConvert',
    ogDescription: 'Try our validation flows before you build. See payment tests, waitlists, and pricing tables in action.'
  },
  '/affiliate': {
    title: 'Affiliate Program - Earn 30% Recurring Commission | WillTheyConvert',
    description: 'Join our affiliate program and earn 30% lifetime commissions on every referral. Share WillTheyConvert with your audience and build passive income.',
    keywords: 'affiliate program, referral program, earn commissions, passive income, startup tools affiliate',
    canonicalUrl: '/affiliate',
    ogTitle: 'WillTheyConvert Affiliate Program | Earn 30% Commission',
    ogDescription: 'Promote WillTheyConvert and earn 30% on every sale. Join our affiliate program today.'
  },
  '/subscription': {
    title: 'Pricing - Start Validating Your Ideas | WillTheyConvert',
    description: 'Simple, affordable pricing to validate your startup ideas. Get lifetime access for just $19. No subscriptions, no hidden fees.',
    keywords: 'pricing, plans, subscription, startup validation pricing, lifetime access',
    canonicalUrl: '/subscription',
    ogTitle: 'Simple Pricing - Lifetime Access for $19 | WillTheyConvert',
    ogDescription: 'One-time payment, lifetime access. Start validating your product ideas today.'
  }
};

const getDynamicSEO = (pathname: string): SEOData => {
  // Handle dynamic routes
  if (pathname.startsWith('/analytics/')) {
    return {
      title: 'Flow Analytics - WillTheyConvert',
      description: 'Detailed analytics and insights for your validation flow.',
      noIndex: true
    };
  }
  if (pathname.startsWith('/edit/')) {
    return {
      title: 'Edit Flow - WillTheyConvert',
      description: 'Edit your validation flow settings and content.',
      noIndex: true
    };
  }
  if (pathname.startsWith('/payment/')) {
    return {
      title: 'Payment Flow - WillTheyConvert',
      description: 'Complete the payment process.',
      noIndex: true
    };
  }
  if (pathname.startsWith('/signup/')) {
    return {
      title: 'Signup Flow - WillTheyConvert',
      description: 'Complete the signup process.',
      noIndex: true
    };
  }
  if (pathname.startsWith('/newsletter/')) {
    return {
      title: 'Newsletter Flow - WillTheyConvert',
      description: 'Subscribe to our newsletter.',
      noIndex: true
    };
  }
  if (pathname.startsWith('/referral/')) {
    return {
      title: 'Referral Flow - WillTheyConvert',
      description: 'Join through referral.',
      noIndex: true
    };
  }
  if (pathname.startsWith('/thank-you/')) {
    return {
      title: 'Thank You - WillTheyConvert',
      description: 'Thank you for your action.',
      noIndex: true
    };
  }
  if (pathname.startsWith('/buy-button/')) {
    return {
      title: 'Buy Button Flow - WillTheyConvert',
      description: 'Complete your purchase.',
      noIndex: true
    };
  }
  if (pathname.startsWith('/pricing-table/')) {
    return {
      title: 'Pricing Table - WillTheyConvert',
      description: 'View our pricing options.',
      noIndex: true
    };
  }

  // Return the static SEO data or default
  return {
    ...DEFAULT_SEO,
    ...ROUTE_SEO[pathname]
  };
};

export const useSEO = () => {
  const { pathname } = useLocation();
  const seoData = getDynamicSEO(pathname);

  return seoData;
}; 